import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import { ApplicationContext } from 'src/app/core/models/application-context.model';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { AdminCompanyAutoSelectService } from 'src/app/core/services/admin-company-auto-select.service';
import { AdminScopeService } from 'src/app/core/services/admin-scope.service';
import { getUserCompanyId } from 'src/app/core/utils/admin-company.util';
import { clearLaunchRequestedCompanyId } from 'src/app/core/utils/admin-launch-context.util';

@Component({
  selector: 'app-user-companies-list',
  templateUrl: './user-companies-list.component.html',
  styleUrls: ['./user-companies-list.component.scss']
})
export class UserCompaniesListComponent implements OnInit {
  userCompanies: any = [];
  filteredCompanies: any = [];
  isLoading: boolean = false;
  returnUrl: string = '';
  currentCompanyName: string;
  isInitialSelection = false;
  isGlobalAdmin = false;
  allOrgsActive = false;

  constructor(
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _appContextService: ApplicationContextService,
    private readonly _authService: AuthenticationService,
    private readonly _adminCompanyAutoSelect: AdminCompanyAutoSelectService,
    private readonly _adminScope: AdminScopeService) {
  }

  ngOnInit() {
    const reason = this._route.snapshot.queryParamMap.get('reason');
    this.isInitialSelection =
      reason === 'multiple-orgs' || reason === 'no-org-context';

    this._appContextService.context.pipe(take(1)).subscribe((context) => {
      this.bindCompanyList(context);
    });
  }

  private bindCompanyList(context: ApplicationContext) {
    this.isGlobalAdmin = !!context.currentlyLoggedInUser?.superGlobalAdmin;
    this.allOrgsActive = this._adminScope.isAllOrganizations;
    this.currentCompanyName = this.allOrgsActive
      ? 'All organizations'
      : context.currentlyLoggedInUser?.companyName;
    this.userCompanies = context.userCompanies
      .filter(res => res.isHidden !== true)
      .sort((a, b) => Number(b.isPinned) - Number(a.isPinned));

    if (this.isInitialSelection) {
      this.filteredCompanies = [...this.userCompanies];
      return;
    }

    this.filteredCompanies = this.userCompanies.filter(
      uc => getUserCompanyId(uc) !== context.currentlyLoggedInUser.companyId
    );
  }

  filterCompanyByName(evt: any) {
    const searchString = (evt.target as HTMLInputElement).value;
    this.filteredCompanies = this.userCompanies.filter((uc: any) => !searchString || uc.company.name.toLowerCase().includes(searchString.toLowerCase()))
  }

  /** Super Admin: cross-company provider / practice queues only. */
  selectAllOrganizations() {
    if (!this.isGlobalAdmin || this.isLoading) {
      return;
    }
    this._adminScope.setScope('all');
    // Drop single-org handoff so queue APIs are not scoped to one company.
    clearLaunchRequestedCompanyId();
    void this._router.navigateByUrl('/provider-profiles');
  }

  companySelected(userId: string, selectedCompany: string) {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this._adminScope.setScope('company');
    this._adminCompanyAutoSelect
      .switchToCompanyIds(userId, selectedCompany)
      .subscribe({
        next: (switched) => {
          if (switched) {
            void this._router.navigateByUrl('/');
            return;
          }
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          console.log(err);
        },
      });
  }

  pinOrHideCompany(selectedUserCompany: any) {
    const request = this.createRequestPayload(selectedUserCompany);
    this._authService.hideOrPinCompany(request).subscribe({
      next: (res) => {
        console.log(res)
      },
      error: (err) => {
        console.log(err);
      }
    });
  }

  onPinClick(selectedUserCompany: any) {
    selectedUserCompany.isPinned = !selectedUserCompany.isPinned;
    this.pinOrHideCompany(selectedUserCompany);
  }

  onHideClick(selectedUserCompany: any) {
    selectedUserCompany.isHidden = true;
    this.pinOrHideCompany(selectedUserCompany);
  }

  private createRequestPayload(selectedUserCompany: any) {
    return {
      userId: selectedUserCompany.userId,
      companyId: selectedUserCompany.company.id,
      isHidden: selectedUserCompany.isHidden,
      isPinned: selectedUserCompany.isPinned,
    }
  }

  returnToHome() {
    if (this._adminScope.isAllOrganizations) {
      void this._router.navigateByUrl('/provider-profiles');
      return;
    }
    this._router.navigateByUrl('/');
  }
}
